import React, { useState } from 'react'
import { styled } from '@mui/material/styles'
import { Navigate } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Snackbar from '@mui/material/Snackbar'
import MuiAlert, { AlertProps } from '@mui/material/Alert'
import { useTranslation } from 'react-i18next'
import { SelectChangeEvent } from '@mui/material'
import {
  useWillPropose,
  useMayPropose,
  useWillNotPropose
} from 'proposals/proposal/queries/useProposalMutations'

const PREFIX = 'ProposalIntent'

const classes = {
  intentArea: `${PREFIX}-intentArea`,
  proposalIntent: `${PREFIX}-proposalIntent`,
  select: `${PREFIX}-select`,
  buttonContainer: `${PREFIX}-buttonContainer`,
  anotherReason: `${PREFIX}-anotherReason`
}

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(({ theme: { breakpoints, spacing, shape } }) => ({
  [`& .${classes.proposalIntent}`]: {
    width: '100%',
    borderRadius: shape.borderRadius,
    backgroundColor: '#EFEFEF',
    padding: spacing(2),
    [breakpoints.up('md')]: {
      maxWidth: 443
    }
  },

  [`& .${classes.select}`]: {
    marginLeft: spacing(4),
    maxWidth: 280
  },

  [`& .${classes.buttonContainer}`]: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: spacing(2)
  },

  [`& .${classes.anotherReason}`]: {
    marginTop: spacing(1)
  }
}))

enum ProposeType {
  WILL_PROPOSE = 'will-propose',
  MAY_PROPOSE = 'may-propose',
  WILL_NOT_PROPOSE = 'will-not-propose'
}

type ProposalIntentPropTypes = {
  recipientId: string
  rfpId: string
}

function Alert(props: AlertProps) {
  return <MuiAlert elevation={6} variant="filled" {...props} />
}

const ProposalIntent: React.FC<ProposalIntentPropTypes> = ({ rfpId, recipientId }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState<boolean>(false)

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }

    setOpen(false)
  }

  const [reason, setReason] = useState<string>('')
  const [anotherReason, setAnotherReason] = useState<string>('')
  const [propose, setPropose] = useState<string>('')

  const reasons = [
    {
      id: '6c04e342-073e-4d63-b36a-e36adaae2f51',
      reason: t('too-much-work-right-now')
    },
    {
      id: '70b12d15-ff19-4147-bf30-ce68e1415322',
      reason: t('type-of-work-is-not-suitable-for-us')
    },
    {
      id: 'f075d71c-e922-4ea8-a0e2-989e0325b483',
      reason: t('target-location-is-difficult')
    },
    {
      id: '0adf367b-8499-46d0-83b8-36beb52f5ea5',
      reason: t('request-for-proposal-would-need-more-information')
    },
    {
      id: '4b0e21f4-f32b-4e2a-b05e-9698c66e5c9f',
      reason: t('required-pricing-is-too-risky')
    },
    {
      id: '7420158c-8f3c-4bf6-916e-c5f24e7da905',
      reason: t('other-reason')
    }
  ]

  const anotherReasonId = '7420158c-8f3c-4bf6-916e-c5f24e7da905'

  const { mutate: sendWillPropose, isError: isSendWillProposeError } = useWillPropose(
    rfpId,
    recipientId,
    () => setOpen(true)
  )

  const { mutate: sendMayPropose, isError: isMayWillProposeError } = useMayPropose(
    rfpId,
    recipientId,
    () => setOpen(true)
  )

  const { mutate: sendWillNotPropose, isError: isWillNotProposeError } = useWillNotPropose(
    rfpId,
    recipientId,
    () => setOpen(true)
  )

  const onProposeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target
    setPropose(value)
    setReason('')
    setAnotherReason('')
  }

  const onReasonChange = (event: SelectChangeEvent<string>): void => {
    const reason = event.target.value
    setReason(reason)
    setAnotherReason('')
  }

  const onAnotherReasonChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setAnotherReason(event.target.value)
  }

  const onSubmit = (): void => {
    if (propose === ProposeType.WILL_PROPOSE) {
      sendWillPropose()
    } else if (propose === ProposeType.MAY_PROPOSE) {
      sendMayPropose()
    } else {
      const reasonValue =
        reason === anotherReasonId ? anotherReason : reasons.find((item) => item.id === reason)?.reason ?? ''
      sendWillNotPropose(reasonValue)
    }
  }

  if (isSendWillProposeError || isMayWillProposeError || isWillNotProposeError) {
    return <Navigate to="/error" replace />
  }

  return (
    <Root>
      <Box displayPrint="none">
        <article className={classes.proposalIntent}>
          <header>
            <Typography paragraph>{t('how-is-it-looking-initially')}</Typography>
          </header>
          <Typography variant="body2" gutterBottom>
            {t('proposal-intent-text')}
          </Typography>
          <FormControl>
            <RadioGroup aria-label="proposal intent" name="proposalIntent" value={propose} onChange={onProposeChange}>
              <FormControlLabel
                value={ProposeType.WILL_PROPOSE}
                control={<Radio color="primary" />}
                label={t('we-will-propose')}
              />
              <FormControlLabel
                value={ProposeType.MAY_PROPOSE}
                control={<Radio color="primary" />}
                label={t('we-may-propose')}
              />
              <FormControlLabel
                value={ProposeType.WILL_NOT_PROPOSE}
                control={<Radio color="primary" />}
                label={t('we-likely-will-not-propose')}
              />
            </RadioGroup>
          </FormControl>
          <div className={classes.select}>
            <FormControl
              variant="filled"
              fullWidth
              disabled={!propose || propose === ProposeType.WILL_PROPOSE || propose === ProposeType.MAY_PROPOSE}
            >
              <InputLabel id="proposal-intent-reason">{t('select-reason')}</InputLabel>
              <Select labelId="proposal-intent-reason" value={reason} onChange={onReasonChange}>
                {reasons.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.reason}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {reason === anotherReasonId && (
              <TextField
                className={classes.anotherReason}
                fullWidth
                variant="filled"
                label={t('reason')}
                value={anotherReason}
                onChange={onAnotherReasonChange}
              />
            )}
          </div>
          <footer className={classes.buttonContainer}>
            <Button color="grey" variant="outlined" onClick={onSubmit} disabled={propose === ''}>
              {t('submit')}
            </Button>
          </footer>
        </article>
      </Box>
      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <div>
          <Alert onClose={handleClose} severity="success">
            {t('initial-answer-saved')}
          </Alert>
        </div>
      </Snackbar>
    </Root>
  )
}

export default ProposalIntent
